import { useTheme } from "@/presentation/contexts/ThemeContext";
import { useResponsiveDesign } from "@/presentation/responsiveDesign/responsiveDesign";
import { qaAttr, type QaAttrOptions } from "@/presentation/utils/qaAttr";

/**
 * GoLiveButton — the web port of Flutter's `GoLiveButtonWidget`
 * (`lib/presentation/custom_widgets/go_live_button_widget.dart`).
 *
 * Modeled on the sibling `AddMediaButton`: a circular action button (the
 * `iconBackgroundColor` disc) whose glyph is a solid "live"/record dot in
 * `dotColor` — exactly what Flutter draws (`Container(shape: circle,
 * color: iconColor)`, `:51-56`), so no asset glyph is involved. Used inline in
 * the community composer's action row (`community_screen.dart:1359-1367`).
 */
interface GoLiveButtonProps {
  backgroundColor?: string;
  iconBackgroundColor?: string;
  /** The centered live dot; defaults to `theme.exclusive` (Flutter `AppColors.exclusive`). */
  dotColor?: string;
  buttonSize?: number;
  dotSize?: number;
  /** If omitted, the disc is rendered as a circle (radius = buttonSize). */
  borderRadius?: number;
  onTap: () => void;
  /**
   * When true, the button renders in a dimmed, non-interactive state — the tap is
   * a no-op, the cursor is `not-allowed`, and the disc is dimmed to
   * `responsive.opacity.disabled`. Additive (defaults to `false`, so existing
   * callers are unaffected). Used by the community composer to keep the go-live
   * affordance VISIBLE-but-inert while the current user is already broadcasting a
   * LIVE session (note-2 guard) — "disable the control + a distinct state, not
   * hide it" (`workflow/lessons.md` § Web browser lifecycle & multi-tab).
   */
  disabled?: boolean;
  /** Optional `data-qa-*` set for the clickable root `<div>` so the qa-tester can locate it. */
  qa?: QaAttrOptions;
}

export const GoLiveButton = ({
  backgroundColor,
  iconBackgroundColor,
  dotColor,
  buttonSize = 28,
  dotSize = 12,
  borderRadius,
  onTap,
  disabled = false,
  qa,
}: GoLiveButtonProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const bgColor = backgroundColor ?? theme.background;
  const iconBgColor = iconBackgroundColor ?? theme.secondary;
  const dot = dotColor ?? theme.exclusive;

  return (
    <div
      {...(qa ? qaAttr({ ...qa, status: disabled ? 'disabled' : 'enabled' }) : {})}
      onClick={disabled ? undefined : onTap}
      aria-disabled={disabled}
      className="flex items-center justify-center"
      style={{
        backgroundColor: bgColor,
        maxHeight: buttonSize,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? responsive.opacity.disabled : 1,
      }}
    >
      <div
        className="flex items-center justify-center"
        style={{
          width: buttonSize,
          height: buttonSize,
          backgroundColor: iconBgColor,
          borderRadius: borderRadius ?? buttonSize,
        }}
      >
        <div
          style={{
            width: dotSize,
            height: dotSize,
            backgroundColor: dot,
            borderRadius: dotSize,
          }}
        />
      </div>
    </div>
  );
};
